import { NgModule } from '@angular/core';
import { ExtraOptions, PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { AdminPage } from './page/admin/admin.page';
import { HostPage } from './page/host/host.page';
import { HostedMeetingsPage } from './page/host/hosted-meetings/hosted-meetings.page';
import { MeetingAddPage } from './page/host/meeting-add/meeting-add.page';
import { MeetingEditPage } from './page/host/meeting-edit/meeting-edit.page';
import { MeetingManagementPage } from './page/host/meeting-management/meeting-management.page';
import { AuthGuard } from './service/auth-guard.service';
import { CanDeactivateGuard } from './service/can-deactivate.guard';
import { RoleGuard } from './service/role-guard';
import { SubdomainRedirectGuard } from './service/subdomain-redirect.guard';

export class RouterConfiguration implements ExtraOptions {
  scrollPositionRestoration: 'enabled' | 'disabled' | 'top' = 'enabled';
  preloadingStrategy: PreloadAllModules;
  relativeLinkResolution: 'legacy';
}

const routes: Routes = [
  {
    path: '.well-known/change-password',
    redirectTo: '/tabs/change-password',
    pathMatch: 'full',
  },
  {
    // Remaining `/tabs/*` routes after the user-facing site was retired.
    // Only authentication flows (reused by admin/host login) and the
    // admin-only blog/product editors live here now.
    path: 'tabs',
    children: [
      {
        path: 'login',
        data: { animationState: 'Child' },
        children: [
          {
            path: '',
            loadChildren: () => import('./page/user/login/login.module').then((m) => m.LoginPageModule),
          },
        ],
      },
      {
        path: 'signup',
        children: [
          {
            path: '',
            loadChildren: () => import('./page/user/signup/signup.module').then((m) => m.SignupPageModule),
          },
        ],
      },
      {
        path: 'find-password',
        loadChildren: () =>
          import('./page/user/find-password/find-password.module').then((m) => m.FindPasswordPageModule),
      },
      {
        path: 'change-password',
        loadChildren: () =>
          import('./page/user/change-password/change-password.module').then((m) => m.ChangePasswordPageModule),
      },
      {
        path: 'reset-password',
        loadChildren: () =>
          import('./page/user/reset-password/reset-password.module').then((m) => m.ResetPasswordPageModule),
      },
      {
        path: 'need-user-info',
        loadChildren: () =>
          import('./page/user/need-user-info/need-user-info.module').then((m) => m.NeedUserInfoPageModule),
      },
      // Blog editor — entered from the admin blog list (/tabs/blog-add, /tabs/blog-edit)
      {
        path: 'blog-add',
        canActivateChild: [AuthGuard, RoleGuard],
        data: { roles: ['admin'] },
        children: [
          {
            path: '',
            loadChildren: () => import('./page/blog/blog-add/blog-add.module').then((m) => m.BlogAddPageModule),
          },
        ],
      },
      {
        path: 'blog-edit/:id',
        canActivateChild: [AuthGuard, RoleGuard],
        data: { roles: ['admin'] },
        children: [
          {
            path: '',
            loadChildren: () => import('./page/blog/blog-edit/blog-edit.module').then((m) => m.BlogEditPageModule),
          },
        ],
      },
    ],
  },
  {
    path: 'admin',
    component: AdminPage,
    canActivateChild: [AuthGuard, RoleGuard],
    children: [
      { path: '', redirectTo: 'payments-v2', pathMatch: 'full' },
      {
        path: 'survey',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-survey/admin-survey.module').then((m) => m.AdminSurveyPageModule),
      },
      // 결제 목록 화면은 payments-v2 하나뿐이다. 이 주소는 운영자 북마크·외부 링크(알림 딥링크
      // /admin/payment?paymentId=N) 때문에 남긴다. 상대 경로여야 쿼리스트링이 보존된다.
      {
        path: 'payment',
        redirectTo: 'payments-v2',
        pathMatch: 'full',
      },
      {
        path: 'payments-v2',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-payment-v2/admin-payment-v2.module').then((m) => m.AdminPaymentV2PageModule),
      },
      {
        path: 'payment-calendar',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-payment-calendar/admin-payment-calendar.module').then(
            (m) => m.AdminPaymentCalendarPageModule,
          ),
      },
      {
        path: 'product',
        redirectTo: 'products-v2',
        pathMatch: 'full',
      },
      {
        path: 'product-add',
        redirectTo: 'products/add',
        pathMatch: 'full',
      },
      // 목록 화면은 products-v2 하나뿐이다. 이 주소들은 운영자 북마크·외부 링크 때문에 남긴다.
      // 상대 경로여야 쿼리스트링이 보존되고, 리다이렉트를 또 거치게 하면 안 된다 — Angular 는 상대
      // 리다이렉트를 한 번만 이어 주므로 두 번째 홉은 건너뛰어지고 자식 없는 라우트에 착지한다.
      {
        path: 'products',
        redirectTo: 'products-v2',
        pathMatch: 'full',
      },
      {
        path: 'products-detail',
        redirectTo: 'products-v2',
        pathMatch: 'full',
      },
      {
        path: 'products-detail/add',
        redirectTo: 'products/add',
        pathMatch: 'full',
      },
      {
        path: 'products-detail/:id/edit',
        redirectTo: 'products/:id/edit',
        pathMatch: 'full',
      },
      {
        path: 'products-detail/:id',
        redirectTo: 'products/:id/edit',
        pathMatch: 'full',
      },
      {
        path: 'products',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-product/admin-product.module').then((m) => m.AdminProductPageModule),
      },
      {
        path: 'products-v2',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-product-v2/admin-product-v2.module').then((m) => m.AdminProductV2PageModule),
      },
      {
        path: 'external-reservations',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-external-reservation/admin-external-reservation.module').then(
            (m) => m.AdminExternalReservationPageModule,
          ),
      },
      {
        path: 'external-products',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-external-product/admin-external-product.module').then(
            (m) => m.AdminExternalProductPageModule,
          ),
      },
      {
        path: 'schedule',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-schedule/admin-schedule.module').then((m) => m.AdminSchedulePageModule),
      },
      {
        // 구 '출발 코스 관리' 북마크가 관리자 홈으로 튕기지 않게 한다.
        path: 'departure-courses',
        redirectTo: 'departures-v2',
        pathMatch: 'full',
      },
      {
        path: 'departures-v2',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-departures-v2/admin-departures-v2.module').then(
            (m) => m.AdminDeparturesV2PageModule,
          ),
      },
      {
        path: 'schedule/add/:id',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-schedule/admin-schedule-add/admin-schedule-add.module').then(
            (m) => m.AdminScheduleAddPagePageModule,
          ),
      },
      {
        path: 'schedule/add/:id/:ticketId',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-schedule/admin-schedule-add/admin-schedule-add.module').then(
            (m) => m.AdminScheduleAddPagePageModule,
          ),
      },
      {
        path: 'schedule/edit/:id',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-schedule/admin-schedule-edit/admin-schedule-edit.module').then(
            (m) => m.AdminScheduleEditPageModule,
          ),
      },
      {
        path: 'users',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-users/admin-users.module').then((m) => m.AdminUsersPageModule),
      },
      {
        // 더 구체적인 라우트가 먼저 — Angular Router는 first-match-wins이라
        // `hosts/:hostInfoId`를 `hosts` 위에 두지 않으면 hosts 모듈로 흡수된다
        path: 'hosts/:hostInfoId',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-host-detail/admin-host-detail.module').then(
            (m) => m.AdminHostDetailPageModule,
          ),
      },
      {
        path: 'hosts',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-hosts/admin-hosts.module').then((m) => m.AdminHostsPageModule),
      },
      {
        path: 'host-change-requests/:id',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-host-change-request/admin-host-change-request.module').then(
            (m) => m.AdminHostChangeRequestPageModule,
          ),
      },
      {
        path: 'user',
        redirectTo: 'users',
        pathMatch: 'full',
      },
      {
        path: 'settlement',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-settlement/admin-settlement.module').then((m) => m.AdminSettlementPageModule),
      },
      {
        path: 'product-order',
        redirectTo: 'display',
        pathMatch: 'full',
      },
      {
        path: 'display',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-display/admin-display.module').then((m) => m.AdminDisplayPageModule),
      },
      {
        path: 'push-campaigns',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-push-campaign/admin-push-campaign.module').then(
            (m) => m.AdminPushCampaignPageModule,
          ),
      },

      {
        path: 'blog',
        data: { roles: ['admin'] },
        loadChildren: () => import('../app/page/admin/admin-blog/admin-blog.module').then((m) => m.AdminBlogPageModule),
      },
      {
        path: 'event',
        redirectTo: 'events',
        pathMatch: 'full',
      },
      {
        path: 'events',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('../app/page/admin/admin-event/admin-event.module').then((m) => m.AdminEventPageModule),
      },
      {
        path: 'inquiry',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-inquiry/admin-inquiry.module').then((m) => m.AdminInquiryPageModule),
      },
      {
        path: 'cs-inquiry',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-cs-inquiry/admin-cs-inquiry.module').then((m) => m.AdminCsInquiryPageModule),
      },
      {
        path: 'audit-logs',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-audit-logs/admin-audit-logs.module').then((m) => m.AdminAuditLogsPageModule),
      },
      {
        path: 'push-logs',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-push-logs/admin-push-logs.module').then((m) => m.AdminPushLogsPageModule),
      },
      {
        path: 'alimtalk-logs',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-alimtalk-logs/admin-alimtalk-logs.module').then(
            (m) => m.AdminAlimtalkLogsPageModule,
          ),
      },
      {
        path: 'display/banners',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-banner/admin-banner.module').then((m) => m.AdminBannerPageModule),
      },
      {
        path: 'banner',
        redirectTo: 'display/banners',
        pathMatch: 'full',
      },
      {
        path: 'campaigns',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-campaign/admin-campaign.module').then((m) => m.AdminCampaignPageModule),
      },
      {
        path: 'ads',
        data: { roles: ['admin'] },
        loadChildren: () => import('./page/admin/admin-ad/admin-ad.module').then((m) => m.AdminAdPageModule),
      },
      {
        path: 'config',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-config/admin-config.module').then((m) => m.AdminConfigPageModule),
      },
      {
        path: 'coupon',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-coupon/admin-coupon.module').then((m) => m.AdminCouponPageModule),
      },
      {
        path: 'coupon-group',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-coupon-group/admin-coupon-group.module').then((m) => m.AdminCouponGroupPageModule),
      },
      {
        path: 'user-group',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-user-group/admin-user-group.module').then((m) => m.AdminUserGroupPageModule),
      },
      {
        path: 'business-groups',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-business-group/admin-business-group.module').then(
            (m) => m.AdminBusinessGroupPageModule,
          ),
      },
      {
        path: 'preorder',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-preorder/admin-preorder.module').then((m) => m.AdminPreorderPageModule),
      },
      {
        path: 'internal-users',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-internal-users/admin-internal-users.module').then(
            (m) => m.AdminInternalUsersPageModule,
          ),
      },
      {
        path: 'alliance',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-alliance/admin-alliance.module').then((m) => m.AdminAlliancePageModule),
      },
      {
        path: 'vendor-profiles',
        data: { roles: ['admin'] },
        loadChildren: () =>
          import('./page/admin/admin-vendor-profiles/admin-vendor-profiles.module').then(
            (m) => m.AdminVendorProfilesPageModule,
          ),
      },
    ],
  },
  {
    path: 'auth',
    children: [
      {
        path: 'link-account',
        loadChildren: () => import('./page/user/link-account/link-account.module').then((m) => m.LinkAccountPageModule),
      },
    ],
  },
  {
    // Legacy redirect for old URL
    path: 'tabs/link-account',
    redirectTo: 'auth/link-account',
    pathMatch: 'full',
  },
  {
    // Ticket group extension page - accessible via magic link without auth
    path: 'host/schedule/extend',
    loadChildren: () =>
      import('./page/host/host-schedule/host-schedule-extend/host-schedule-extend.module').then(
        (m) => m.HostScheduleExtendPageModule,
      ),
  },
  {
    path: 'host',
    component: HostPage,
    canActivateChild: [AuthGuard],
    children: [
      { path: '', redirectTo: 'main', pathMatch: 'full' },
      {
        path: 'main',
        loadChildren: () => import('./page/host/host-main/host-main.module').then((m) => m.HostMainPageModule),
      },
      {
        path: 'meeting-management',
        redirectTo: 'products',
        pathMatch: 'full',
      },
      {
        path: 'meeting-management/hosted-meetings',
        redirectTo: 'products',
        pathMatch: 'full',
      },
      {
        path: 'meeting-management/meeting-add',
        redirectTo: 'products/add',
        pathMatch: 'full',
      },
      {
        path: 'meeting-management/meeting-edit/:id',
        redirectTo: 'products/:id/edit',
        pathMatch: 'full',
      },
      {
        path: 'products',
        component: MeetingManagementPage,
        children: [
          { path: '', component: HostedMeetingsPage },
          {
            path: 'add',
            component: MeetingAddPage,
            canDeactivate: [CanDeactivateGuard],
          },
          //TODO add user is host guard
          {
            path: ':id/edit',
            component: MeetingEditPage,
            canDeactivate: [CanDeactivateGuard],
          },
        ],
      },
      {
        path: 'reservation',
        loadChildren: () => import('./page/host/host-payment/host-payment.module').then((m) => m.HostPaymentPageModule),
      },
      {
        path: 'schedule/daily',
        loadChildren: () =>
          import('./page/host/host-schedule-daily/host-schedule-daily.module').then(
            (m) => m.HostScheduleDailyPageModule,
          ),
      },
      {
        path: 'schedule',
        loadChildren: () =>
          import('../app/page/host/host-schedule/host-schedule.module').then((m) => m.HostSchedulePageModule),
      },
      {
        path: 'schedule/add/:id',
        loadChildren: () =>
          import('./page/host/host-schedule/host-schedule-add/host-schedule-add.module').then(
            (m) => m.HostScheduleAddPagePageModule,
          ),
      },
      {
        path: 'schedule/add/:id/:ticketId',
        loadChildren: () =>
          import('./page/host/host-schedule/host-schedule-add/host-schedule-add.module').then(
            (m) => m.HostScheduleAddPagePageModule,
          ),
      },
      {
        path: 'schedule/edit/:id',
        loadChildren: () =>
          import('./page/host/host-schedule/host-schedule-edit/host-schedule-edit.module').then(
            (m) => m.HostScheduleEditPageModule,
          ),
      },
      {
        path: 'review',
        loadChildren: () => import('./page/host/host-review/host-review.module').then((m) => m.HostReviewPageModule),
      },
      {
        path: 'settlement',
        loadChildren: () =>
          import('./page/host/host-settlement/host-settlement.module').then((m) => m.HostSettlementPageModule),
      },
      {
        path: 'stat',
        loadChildren: () => import('./page/host/host-stat/host-stat.module').then((m) => m.HostStatPageModule),
      },
      {
        path: 'info',
        loadChildren: () => import('./page/host/host-info/host-info.module').then((m) => m.HostInfoPageModule),
      },
      {
        path: 'blog',
        loadChildren: () => import('./page/host/host-blog/host-blog.module').then((m) => m.HostBlogPageModule),
      },
      {
        path: 'blog/:id',
        loadChildren: () =>
          import('./page/host/host-blog/host-blog-detail/host-blog-detail.module').then(
            (m) => m.HostBlogDetailPageModule,
          ),
      },
      {
        path: 'inquiry',
        loadChildren: () => import('./page/host/host-inquiry/host-inquiry.module').then((m) => m.HostInquiryPageModule),
      },
      {
        path: 'inquiry/:id',
        loadChildren: () =>
          import('./page/host/host-inquiry/host-inquiry-detail/host-inquiry-detail.module').then(
            (m) => m.HostInquiryDetailPageModule,
          ),
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./page/host/host-settings/host-settings.module').then((m) => m.HostSettingsPageModule),
      },
    ],
  },
  {
    // 권한 없는 사용자의 종착지. 가드를 걸면 안 된다 — 여기로 보내진 사용자는
    // 정의상 권한 판정에 실패한 상태라, 가드가 다시 튕겨내면 루프가 재발한다.
    path: 'forbidden',
    loadChildren: () => import('./page/forbidden/forbidden.module').then((m) => m.ForbiddenPageModule),
  },
  {
    path: '**',
    canActivate: [SubdomainRedirectGuard],
    children: [],
  },
];
@NgModule({
  imports: [RouterModule.forRoot(routes, new RouterConfiguration())],
  exports: [RouterModule],
})
export class AppRoutingModule {}
